import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  Euro,
  CheckCircle,
  Info,
  TrendingUp,
  TrendingDown,
  LocalShipping,
  Groups,
  Inventory,
  Build,
  DirectionsCarFilled,
  Warning
} from '@mui/icons-material';

function MovingCosts() {
  const [activeTab, setActiveTab] = useState(0);

  const costFactors = [
    {
      factor: 'Umzugsvolumen',
      impact: 'Hoch',
      description: 'Je mehr transportiert wird, desto höher die Kosten'
    },
    {
      factor: 'Entfernung',
      impact: 'Hoch',
      description: 'Kilometerpauschale und Fahrtzeit beeinflussen den Preis'
    },
    {
      factor: 'Etage & Aufzug',
      impact: 'Mittel',
      description: 'Höhere Etagen ohne Aufzug erhöhen die Kosten'
    },
    {
      factor: 'Zeitpunkt',
      impact: 'Mittel',
      description: 'Wochenende und Monatsende sind teurer'
    },
    {
      factor: 'Zusatzleistungen',
      impact: 'Variabel',
      description: 'Packen, Montage, Einlagerung kosten extra'
    }
  ];

  const averageCosts = [
    {
      type: '1-Zimmer-Wohnung',
      volume: '10-15 m³',
      local: '300-500 €',
      longDistance: '500-1.200 €'
    },
    {
      type: '2-Zimmer-Wohnung',
      volume: '20-30 m³',
      local: '500-800 €',
      longDistance: '800-1.800 €'
    },
    {
      type: '3-Zimmer-Wohnung',
      volume: '35-50 m³',
      local: '750-1.200 €',
      longDistance: '1.200-2.500 €'
    },
    {
      type: '4-Zimmer-Wohnung',
      volume: '60-80 m³',
      local: '1.000-1.600 €',
      longDistance: '1.800-3.500 €'
    },
    {
      type: 'Einfamilienhaus',
      volume: '80-120 m³',
      local: '1.500-2.500 €',
      longDistance: '2.500-5.000 €'
    }
  ];

  const additionalCosts = [
    {
      service: 'Umzugskartons (pro Stück)',
      price: '2-5 €',
      icon: <Inventory sx={{ color: '#55d6dc' }} />
    },
    {
      service: 'Packservice (pro m³)',
      price: '20-35 €',
      icon: <Groups sx={{ color: '#55d6dc' }} />
    },
    {
      service: 'Möbelmontage (pro Stunde)',
      price: '40-60 €',
      icon: <Build sx={{ color: '#55d6dc' }} />
    },
    {
      service: 'Halteverbotszone (pro Tag)',
      price: '70-150 €',
      icon: <DirectionsCarFilled sx={{ color: '#55d6dc' }} />
    },
    {
      service: 'Klaviertransport',
      price: '200-500 €',
      icon: <LocalShipping sx={{ color: '#55d6dc' }} />
    },
    {
      service: 'Außenaufzug (pro Tag)',
      price: '200-400 €',
      icon: <TrendingUp sx={{ color: '#55d6dc' }} />
    }
  ];

  const savingTips = [
    {
      category: 'Zeitpunkt',
      tips: [
        'Umzug unter der Woche planen (20-30% günstiger)',
        'Nebensaison nutzen (November-Februar)',
        'Monatsanfang/-mitte statt Monatsende',
        'Frühzeitig buchen für bessere Preise'
      ]
    },
    {
      category: 'Eigenleistung',
      tips: [
        'Selbst packen spart 20-30% der Kosten',
        'Möbel selbst ab- und aufbauen',
        'Kleinere Gegenstände selbst transportieren',
        'Freunde und Familie um Hilfe bitten'
      ]
    },
    {
      category: 'Organisation',
      tips: [
        'Mehrere Angebote einholen und vergleichen',
        'Festpreise statt Stundensätze vereinbaren',
        'Umzugsvolumen realistisch einschätzen',
        'Unnötiges vorher aussortieren'
      ]
    },
    {
      category: 'Steuerlich',
      tips: [
        'Umzugskosten als Werbungskosten absetzen',
        'Handwerkerleistungen steuerlich geltend machen',
        'Belege und Rechnungen aufbewahren',
        'Umzugskostenpauschale nutzen'
      ]
    }
  ];

  const hiddenCosts = [
    {
      item: 'Doppelte Miete',
      description: 'Überlappung von alter und neuer Miete',
      tip: 'Kündigungsfristen optimal planen'
    },
    {
      item: 'Renovierung',
      description: 'Schönheitsreparaturen in alter Wohnung',
      tip: 'Mietvertrag auf Klauseln prüfen'
    },
    {
      item: 'Kaution',
      description: 'Neue Kaution vor Rückzahlung der alten',
      tip: 'Mietkautionskonto oder Bürgschaft nutzen'
    },
    {
      item: 'Ummeldungen',
      description: 'Gebühren für Behörden und Anbieter',
      tip: 'Alle Kosten im Voraus einplanen'
    },
    {
      item: 'Versicherung',
      description: 'Transportversicherung für Schäden',
      tip: 'Versicherungsschutz prüfen und erweitern'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Euro sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Umzugskosten
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Transparent kalkuliert - fair abgerechnet
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Erfahren Sie, welche Kosten bei einem Umzug auf Sie zukommen und wie Sie clever sparen können. 
            Mit unserer Übersicht behalten Sie Ihr Budget im Blick.
          </Typography>
        </Box>

        {/* Cost Overview Alert */}
        <Alert 
          severity="info" 
          sx={{ 
            mb: 6, 
            bgcolor: 'rgba(85, 214, 220, 0.1)', 
            border: '1px solid rgba(85, 214, 220, 0.3)',
            color: '#55d6dc'
          }}
          icon={<Info sx={{ color: '#55d6dc' }} />}
        >
          <Typography variant="body1">
            Die tatsächlichen Umzugskosten hängen von vielen individuellen Faktoren ab. 
            Nutzen Sie unseren <strong>kostenlosen Umzugsrechner</strong> für eine genaue Kalkulation!
          </Typography>
        </Alert>

        {/* Average Costs Table */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Durchschnittliche Umzugskosten
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#1a3a44', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#275664' }}>
                  <TableCell sx={{ color: '#55d6dc', fontWeight: 'bold' }}>Wohnungsgröße</TableCell>
                  <TableCell sx={{ color: '#55d6dc', fontWeight: 'bold' }}>Volumen</TableCell>
                  <TableCell sx={{ color: '#55d6dc', fontWeight: 'bold' }}>Lokal (&lt;50km)</TableCell>
                  <TableCell sx={{ color: '#55d6dc', fontWeight: 'bold' }}>Fernumzug (&gt;100km)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {averageCosts.map((row, index) => (
                  <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                    <TableCell sx={{ color: 'white' }}>{row.type}</TableCell>
                    <TableCell sx={{ color: 'white', opacity: 0.8 }}>{row.volume}</TableCell>
                    <TableCell sx={{ color: '#66bb6a', fontWeight: 'bold' }}>{row.local}</TableCell>
                    <TableCell sx={{ color: '#ffd700', fontWeight: 'bold' }}>{row.longDistance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="body2" sx={{ color: 'white', opacity: 0.7, mt: 2, textAlign: 'center' }}>
            * Preise verstehen sich inklusive MwSt. für einen Standard-Umzug mit 2-3 Helfern
          </Typography>
        </Box>

        {/* Cost Factors */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Was beeinflusst die Kosten?
          </Typography>
          <Grid container spacing={3}>
            {costFactors.map((factor, index) => (
              <Grid item xs={12} key={index}>
                <Card sx={{
                  bgcolor: '#275664',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                          {factor.factor}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                          {factor.description}
                        </Typography>
                      </Box>
                      <Chip 
                        label={factor.impact}
                        size="small"
                        sx={{ 
                          bgcolor: factor.impact === 'Hoch' ? '#ff6b6b' : factor.impact === 'Mittel' ? '#ffd700' : '#55d6dc',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Additional Services */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Zusatzleistungen & Extras
          </Typography>
          <Grid container spacing={3}>
            {additionalCosts.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper sx={{
                  p: 3,
                  bgcolor: '#1a3a44',
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%'
                }}>
                  <Stack spacing={2} alignItems="center" textAlign="center">
                    {service.icon}
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {service.service}
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#ffd700', fontWeight: 'bold' }}>
                      {service.price}
                    </Typography>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Saving Tips Tabs */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Spartipps für Ihren Umzug
          </Typography>
          <Paper sx={{ bgcolor: '#1a3a44' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': { color: 'white' },
                '& .Mui-selected': { color: '#55d6dc' }
              }}
            >
              {savingTips.map((category, index) => (
                <Tab key={index} label={category.category} />
              ))}
            </Tabs>
            <Box p={3}>
              {savingTips[activeTab] && (
                <List>
                  {savingTips[activeTab].tips.map((tip, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <TrendingDown sx={{ color: '#66bb6a' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={tip}
                        sx={{ color: 'white' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Hidden Costs Warning */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Versteckte Kosten vermeiden
          </Typography>
          <Grid container spacing={3}>
            {hiddenCosts.map((cost, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{
                  bgcolor: 'rgba(255,107,107,0.1)',
                  border: '1px solid rgba(255,107,107,0.3)',
                  height: '100%'
                }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} mb={2}>
                      <Warning sx={{ color: '#ff6b6b' }} />
                      <Typography variant="h6" sx={{ color: '#ff6b6b', fontWeight: 'bold' }}>
                        {cost.item}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, mb: 2 }}>
                      {cost.description}
                    </Typography>
                    <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 2 }} />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CheckCircle sx={{ color: '#66bb6a', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#66bb6a' }}>
                        {cost.tip}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: 'rgba(85,214,220,0.1)', 
          border: '2px solid #55d6dc',
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Kostenlose & unverbindliche Beratung
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Lassen Sie sich ein individuelles Angebot erstellen - transparent und ohne versteckte Kosten
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Box
              component="button"
              onClick={() => window.location.href = '/calculator'}
              style={{
                backgroundColor: '#55d6dc',
                color: '#275664',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
            >
              Jetzt Kosten berechnen
            </Box>
            <Box
              component="button"
              onClick={() => window.location.href = '/contact'}
              style={{
                backgroundColor: 'transparent',
                color: '#55d6dc',
                padding: '12px 32px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                border: '2px solid #55d6dc',
                borderRadius: 4,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(85,214,220,0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Angebot anfordern
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export default MovingCosts;